/**
 * Endpoints #23-#27 and #29 — the view screen (summary, metrics, posts, feed, profile, export).
 *
 * Filter/range params are serialized once, in `viewFilterQuery`, exactly as §5.2 specifies:
 * CSV enum lists, `1`/`0` booleans, epoch-ms bounds. `countedPostTypes` is deliberately absent —
 * it is a setting, never a request param.
 */

import type {
  ExportQuery,
  FeedQuery,
  FeedResponse,
  MetricsQuery,
  MetricsResponse,
  PostsQuery,
  PostsResponse,
  ProfileModeQuery,
  ProfileModeResponse,
  SubjectType,
  ViewFilterParams,
  ViewSummaryResponse,
} from '@lw/shared';
import { perfConditionToken } from '@lw/shared';
import { apiJson, apiUrl, type QueryParams } from './client';

function viewPath(t: SubjectType, id: number, suffix = ''): string {
  return `/views/${t}/${id}${suffix}`;
}

/** Shared filter/range params (§5.2). */
export function viewFilterQuery(params: Partial<ViewFilterParams> = {}): QueryParams {
  return {
    from: params.from,
    to: params.to,
    postTypes: params.postTypes,
    contentTypes: params.contentTypes,
    // `subtypes` has no FilterPanel control (API-only, §7.4) but must still be forwarded — direct
    // API and state-level callers set it, and a param the query key changes must reach the server.
    subtypes: params.subtypes,
    edited: params.edited,
    hasLink: params.hasLink,
    authorIds: params.authorIds,
    // Conditions become their `metric:x|n:min` tokens here, the one place §5.2 wire spellings live.
    perf: params.perf?.map(perfConditionToken),
    // The pre-`perf` pair: no control writes it any more, but a direct caller (or a bookmarked
    // URL replayed through the state layer) may still carry it, and a param the query key
    // changes must reach the server.
    perfMetric: params.perfMetric,
    perfMin: params.perfMin,
    lenUnit: params.lenUnit,
    lenMin: params.lenMin,
    lenMax: params.lenMax,
    hasMedia: params.hasMedia,
    /** Saved-posts membership tri-state (SPEC `saved-posts`); absent = no narrowing. */
    saved: params.saved,
    q: params.q,
  };
}

/** #23 `GET /api/views/:t/:id/summary` */
export function getViewSummary(
  t: SubjectType,
  id: number,
  signal?: AbortSignal,
): Promise<ViewSummaryResponse> {
  return apiJson<ViewSummaryResponse>(viewPath(t, id, '/summary'), { signal });
}

/** #24 `GET /api/views/:t/:id/metrics` */
export function getViewMetrics(
  t: SubjectType,
  id: number,
  params: Partial<MetricsQuery> = {},
  signal?: AbortSignal,
): Promise<MetricsResponse> {
  return apiJson<MetricsResponse>(viewPath(t, id, '/metrics'), {
    query: {
      ...viewFilterQuery(params),
      topMetric: params.topMetric,
      sections: params.sections,
      granularity: params.granularity,
    },
    signal,
  });
}

/** #25 `GET /api/views/:t/:id/posts` — offset window, `total` sizes the virtual scroller. */
export function getViewPosts(
  t: SubjectType,
  id: number,
  params: Partial<PostsQuery> = {},
  signal?: AbortSignal,
): Promise<PostsResponse> {
  return apiJson<PostsResponse>(viewPath(t, id, '/posts'), {
    query: {
      ...viewFilterQuery(params),
      sort: params.sort,
      dir: params.dir,
      offset: params.offset,
      limit: params.limit,
    },
    signal,
  });
}

/**
 * #26 `GET /api/views/:t/:id/feed` — keyset pagination via the opaque `cursor`. `sort`/`dir` are
 * the same pair #25 takes, so the header Sort control reorders the feed by any sortable column.
 */
export function getViewFeed(
  t: SubjectType,
  id: number,
  params: Partial<FeedQuery> = {},
  signal?: AbortSignal,
): Promise<FeedResponse> {
  return apiJson<FeedResponse>(viewPath(t, id, '/feed'), {
    query: {
      ...viewFilterQuery(params),
      sort: params.sort,
      dir: params.dir,
      cursor: params.cursor,
      limit: params.limit,
    },
    signal,
  });
}

/** #27 `GET /api/views/:t/:id/profile` — ignores filter/sort/range by design (PRD). */
export function getViewProfile(
  t: SubjectType,
  id: number,
  params: Partial<ProfileModeQuery> = {},
  signal?: AbortSignal,
): Promise<ProfileModeResponse> {
  return apiJson<ProfileModeResponse>(viewPath(t, id, '/profile'), {
    query: { memberId: params.memberId },
    signal,
  });
}

/**
 * #29 `GET /api/views/:t/:id/posts/export` — a streamed file download, so this returns the URL
 * for an `<a download>` / `window.location` rather than fetching it.
 */
export function viewPostsExportUrl(
  t: SubjectType,
  id: number,
  params: Partial<ExportQuery> = {},
): string {
  return apiUrl(viewPath(t, id, '/posts/export'), {
    ...viewFilterQuery(params),
    sort: params.sort,
    dir: params.dir,
    format: params.format,
    columns: params.columns,
  });
}
